import React, { useEffect, useRef } from "react";
import { Send, Paperclip, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RecipientPicker from "@/components/messages/RecipientPicker";
import {
  useGetOwnPhoneNumbersQuery,
  useSendMessageMutation,
  usePostFileMutation,
  useGetSmsEligibilityQuery,
} from "@/store/rest_api";
import OutboundGuardDialogs from "@/components/compliance/OutboundGuardDialogs";
import SmsBlockedNotice from "@/components/messages/SmsBlockedNotice";
import DoNotTextNotice from "@/components/messages/DoNotTextNotice";
import { useOutboundGuard } from "@/hooks/useOutboundGuard";
import { useTextBlockingEntry } from "@/hooks/useTextBlockingEntry";
import { isSmsSendable, isTollFree } from "@/util/a2p";
import { TOLL_FREE_TEXT_CLASS } from "@/components/phone-numbers/TollFreeBadge";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { toast } from "sonner";

export default function NewMessageForm() {
  const { pendingRecipient, setPendingRecipient, composeDraft, setComposeDraft } = useMessageDialog();
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [uploadFile, { isLoading: isUploading }] = usePostFileMutation();
  const outboundGuard = useOutboundGuard();
  const { data: smsEligibility } = useGetSmsEligibilityQuery();
  const textBlockingEntry = useTextBlockingEntry([composeDraft.toNumber]);
  const orgApproved = !!smsEligibility?.orgApproved;

  const { toNumber, fromPhoneNumberId, body, mediaUrls } = composeDraft;
  const setToNumber = (value: string) => setComposeDraft((current) => ({ ...current, toNumber: value }));
  const setFromPhoneNumberId = (value: string) =>
    setComposeDraft((current) => ({ ...current, fromPhoneNumberId: value }));
  const setBody = (value: string) => setComposeDraft((current) => ({ ...current, body: value }));
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeNumbers = phoneNumbers.filter((pn) => pn.active);
  const selectedFromNumber = activeNumbers.find((pn) => pn.id === fromPhoneNumberId);

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!toNumber.trim() || !fromPhoneNumberId) {
      return;
    }
    if (!trimmed && mediaUrls.length === 0) {
      return;
    }

    try {
      await outboundGuard.guard(async (options) => {
        await sendMessage({
          fromPhoneNumber: { id: fromPhoneNumberId },
          toNumbers: [toNumber.trim()],
          body: trimmed || undefined,
          mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
          ...options,
        }).unwrap();

        setComposeDraft((current) => ({ ...current, body: "", mediaUrls: [] }));
        toast.success("Message sent");
        textareaRef.current?.focus();
      });
    } catch (e: any) {
      toast.error(e?.detail || "Failed to send message");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const result = await uploadFile({ file }).unwrap();
      if (result?.url) {
        setComposeDraft((current) => ({ ...current, mediaUrls: [...current.mediaUrls, result.url] }));
      }
    } catch {
      toast.error("Failed to upload file");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeMedia = (index: number) => {
    setComposeDraft((current) => ({
      ...current,
      mediaUrls: current.mediaUrls.filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    if (activeNumbers.length > 0 && !fromPhoneNumberId) {
      setFromPhoneNumberId(activeNumbers[0].id);
    }
  }, [activeNumbers, fromPhoneNumberId]);

  useEffect(() => {
    if (pendingRecipient) {
      setToNumber(pendingRecipient);
      setPendingRecipient(null);
      textareaRef.current?.focus();
    }
  }, [pendingRecipient, setPendingRecipient]);

  return (
    <div className="flex flex-col gap-3">
      <OutboundGuardDialogs guard={outboundGuard} actionLabel="Send anyway" />

      <SmsBlockedNotice fromPhoneNumber={selectedFromNumber} />

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">To</span>
        <RecipientPicker value={toNumber} onChange={setToNumber} />
      </div>

      <DoNotTextNotice blockingEntry={textBlockingEntry} />

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">From</span>
        <Select value={fromPhoneNumberId} onValueChange={setFromPhoneNumberId}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select a number" />
          </SelectTrigger>
          <SelectContent>
            {activeNumbers.map((pn) => {
              const sendable = isSmsSendable(pn, orgApproved);
              return (
                <SelectItem
                  key={pn.id}
                  value={pn.id}
                  disabled={!sendable}
                  className={isTollFree(pn) ? TOLL_FREE_TEXT_CLASS : undefined}
                >
                  {pn.alias ? `${pn.alias} (${pn.number})` : pn.number}
                  {!sendable ? (isTollFree(pn) ? " (not verified)" : " (not enrolled)") : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {mediaUrls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {mediaUrls.map((url, index) => (
            <div key={url} className="relative">
              <img src={url} alt="attachment" className="h-14 w-14 rounded border object-cover" />
              <button
                onClick={() => removeMedia(index)}
                className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Write a message"
        className="min-h-[96px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          title="Attach a file"
        >
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,application/pdf"
          className="hidden"
          onChange={handleFileSelect}
        />
        <Button
          size="sm"
          className="ml-auto"
          onClick={handleSend}
          disabled={
            isSending ||
            !toNumber.trim() ||
            !fromPhoneNumberId ||
            (!body.trim() && mediaUrls.length === 0) ||
            !!textBlockingEntry
          }
        >
          {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Send
        </Button>
      </div>
    </div>
  );
}
